/**
 * Centralized Barcode Label Printing Service
 * 
 * This service provides unified barcode label printing functionality across the application.
 * All printing uses the HTML template defined in qzStore for consistency.
 */

import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/stores/authStore';
import { useQzStore } from '@/stores/qzStore';

const toast = useToast();

/**
 * Print stock barcode label(s)
 * @param {Object} options - Printing options
 * @param {string|Array<string>} options.stockIds - Single stock ID or array of stock IDs
 * @param {number} options.copies - Number of copies to print (default: 1)
 * @returns {Promise<Object>} Print result
 */
export async function printStockBarcodeLabel(options = {}) {
  const {
    stockIds,
    copies = 1
  } = options;

  try {
    const authStore = useAuthStore();
    const qzStore = useQzStore();
    
    const token = authStore.getActiveToken();
    if (!token) {
      toast.error('Authentication required to print barcode labels');
      return { success: false, error: 'No authentication token' };
    }

    // Determine if we're printing single or multiple labels
    const isBulk = Array.isArray(stockIds);
    const stockIdList = isBulk ? stockIds : [stockIds];

    // Fetch barcode label data from API
    const endpoint = '/api/Stock/print-barcode-label';
    const response = await axios.post(endpoint, {
      stockIds: stockIdList,
      copies
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status !== 200 || !response.data) {
      throw new Error('Failed to fetch barcode label data');
    }

    const labelData = response.data;

    // Convert API response to format expected by qzStore.printBarcode
    const products = Array.isArray(labelData) ? labelData : [labelData];

    // Print each product using qzStore's centralized printBarcode function
    for (const item of products) {
      const product = {
        Barcodes: [{ Barcode: item.barcode || item.Barcode }],
        barcode: item.barcode || item.Barcode,
        RetailSku: item.retail_sku || item.retailSku || item.RetailSku,
        sku: item.sku || item.Sku,
        ItemName: item.item_name || item.itemName || item.ItemName || item.Name,
        Name: item.item_name || item.itemName || item.ItemName || item.Name,
        Branches: [{ Price: item.price_list_rate || item.price || item.Price }],
        Price: item.price_list_rate || item.price || item.Price,
        price: item.price_list_rate || item.price || item.Price
      };

      // Print the specified number of copies
      for (let i = 0; i < copies; i++) {
        await qzStore.printBarcode(product);
      }
    }

    toast.success(`${isBulk ? 'Bulk labels' : 'Label'} printed successfully`);
    return { success: true, method: 'qzStore', count: stockIdList.length };

  } catch (error) {
    console.error('Error printing stock barcode label:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to print barcode label';
    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Print BSUR (buyback) barcode label
 * @param {Object} options - Printing options
 * @param {string} options.bsurId - BSUR item ID
 * @param {number} options.copies - Number of copies to print (default: 1)
 * @returns {Promise<Object>} Print result
 */
export async function printBSURBarcodeLabel(options = {}) {
  const {
    bsurId,
    copies = 1
  } = options;

  try {
    const authStore = useAuthStore();
    const qzStore = useQzStore();
    
    const token = authStore.getActiveToken();
    if (!token) {
      toast.error('Authentication required to print BSUR barcode');
      return { success: false, error: 'No authentication token' };
    }

    if (!bsurId) {
      toast.error('BSUR ID is required');
      return { success: false, error: 'Missing BSUR ID' };
    }

    // Fetch BSUR barcode label data from API
    const endpoint = `/api/BSUR/print-barcode-label/${bsurId}`;
    const response = await axios.get(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status !== 200 || !response.data) {
      throw new Error('Failed to fetch BSUR barcode label data');
    }

    const labelData = response.data;
    
    // Convert API response to format expected by qzStore.printBarcode
    const product = {
      Barcodes: [{ Barcode: labelData.barcode || labelData.Barcode }],
      barcode: labelData.barcode || labelData.Barcode,
      RetailSku: labelData.retail_sku || labelData.retailSku || labelData.RetailSku,
      sku: labelData.sku || labelData.Sku,
      ItemName: labelData.item_name || labelData.itemName || labelData.ItemName || labelData.Name || 'BSUR Item',
      Name: labelData.item_name || labelData.itemName || labelData.ItemName || labelData.Name || 'BSUR Item',
      Branches: [{ Price: labelData.price_list_rate || labelData.price || labelData.Price }],
      Price: labelData.price_list_rate || labelData.price || labelData.Price,
      price: labelData.price_list_rate || labelData.price || labelData.Price
    };

    // Print the specified number of copies
    for (let i = 0; i < copies; i++) {
      await qzStore.printBarcode(product);
    }

    toast.success('BSUR label printed successfully');
    return { success: true, method: 'qzStore' };

  } catch (error) {
    console.error('Error printing BSUR barcode label:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to print BSUR barcode';
    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Print product barcode directly (without API call)
 * Uses the existing qzStore.printBarcode method
 * @param {Object} product - Product object with barcode data
 * @returns {Promise<Object>} Print result
 */
export async function printProductBarcode(product) {
  try {
    const qzStore = useQzStore();
    
    if (!product) {
      toast.error('Product data is required');
      return { success: false, error: 'Missing product data' };
    }

    const barcode = product.Barcodes?.[0]?.Barcode || product.barcode;
    if (!barcode) {
      toast.error('No barcode available for this product');
      return { success: false, error: 'Missing barcode' };
    }

    // Use the existing qzStore.printBarcode method
    await qzStore.printBarcode(product);
    
    return { success: true, method: 'qzStore' };

  } catch (error) {
    console.error('Error printing product barcode:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Batch print multiple product barcodes
 * @param {Array<Object>} products - Array of product objects
 * @param {Object} options - Print options
 * @returns {Promise<Object>} Batch print result
 */
export async function batchPrintProductBarcodes(products, options = {}) {
  const { copies = 1 } = options;
  
  if (!Array.isArray(products) || products.length === 0) {
    toast.error('No products provided for batch printing');
    return { success: false, error: 'No products' };
  }

  try {
    const results = [];
    
    for (const product of products) {
      const result = await printProductBarcode(product);
      results.push({ product, result });
      
      // Small delay between prints to avoid overwhelming the printer
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const successCount = results.filter(r => r.result.success).length;
    const failCount = results.length - successCount;

    if (successCount > 0) {
      toast.success(`${successCount} label(s) printed successfully`);
    }
    
    if (failCount > 0) {
      toast.warning(`${failCount} label(s) failed to print`);
    }

    return {
      success: successCount > 0,
      total: results.length,
      successCount,
      failCount,
      results
    };

  } catch (error) {
    console.error('Error in batch barcode printing:', error);
    toast.error('Batch printing failed');
    return { success: false, error: error.message };
  }
}

/**
 * Utility: Check if thermal printing is available
 * @returns {boolean} True if QZ Tray printing is enabled, thermal printer is configured, and QZ is connected
 */
export function isThermalPrintingAvailable() {
  const qzStore = useQzStore();
  return qzStore.useQzTrayPrint && qzStore.isQzConnected && !!qzStore.selectedBarcodePrinter;
}

/**
 * Utility: Get current printer configuration
 * @returns {Object} Current printer configuration
 */
export function getCurrentPrinterConfig() {
  const qzStore = useQzStore();
  
  return {
    useQzTrayPrint: qzStore.useQzTrayPrint,
    hasThermalPrinter: !!qzStore.selectedBarcodePrinter,
    hasRegularPrinter: !!qzStore.selectedPrinter,
    isQzConnected: qzStore.isQzConnected,
    thermalPrinter: qzStore.selectedBarcodePrinter,
    regularPrinter: qzStore.selectedPrinter,
    printWithoutPrice: qzStore.printWithoutPrice
  };
}

export default {
  printStockBarcodeLabel,
  printBSURBarcodeLabel,
  printProductBarcode,
  batchPrintProductBarcodes,
  isThermalPrintingAvailable,
  getCurrentPrinterConfig
};
